import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiMapPin, 
  FiClock, 
  FiBriefcase, 
  FiTrendingUp,
  FiUsers,
  FiAward,
  FiHeart,
  FiZap,
  FiTarget,
  FiStar,
  FiChevronRight,
  FiMail,
  FiPhone,
  FiLinkedin,
  FiSearch,
  FiChevronDown,
  FiDollarSign
} from 'react-icons/fi';

interface Job { 
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  salary: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
}

export const CareersPage: React.FC = () => {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All Departments');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profession: '',
    resume: null as File | null
  });

  const jobs: Job[] = [
    {
      id: '1',
      title: 'Senior SEO Specialist',
      department: 'SEO',
      location: 'Surat, Gujarat',
      type: 'Full-time',
      experience: '3-5 years',
      salary: 'Competitive & Experience-Based',
      description: 'Summirow Digital is looking for a committed and passionate SEO Specialist to join our in-house team. We are seeking professionals who are eager to deliver result-driven campaigns, learn advanced techniques, and grow under expert mentorship.',
      responsibilities: [
        'Develop and execute comprehensive SEO strategies for clients',
        'Conduct keyword research and competitive analysis',
        'Optimize on-page and technical SEO elements',
        'Monitor and report on SEO performance metrics',
        'Lead client presentations and strategy sessions',
        'Stay updated with latest SEO trends and algorithm changes'
      ],
      requirements: [
        '3+ years of proven SEO experience',
        'Strong knowledge of SEO tools (Ahrefs, SEMrush, Google Analytics)',
        'Experience with technical SEO and site audits',
        'Excellent analytical and communication skills',
        'Portfolio of successful SEO campaigns'
      ],
      skills: ['SEO', 'Google Analytics', 'Keyword Research', 'Link Building', 'Content Strategy']
    },
    {
      id: '2',
      title: 'Paid Ads Manager',
      department: 'Paid Marketing',
      location: 'Surat, Gujarat',
      type: 'Full-time',
      experience: '2-4 years',
      salary: 'Competitive & Experience-Based',
      description: 'We are looking for a skilled Paid Ads Manager to manage and optimize advertising campaigns across Google, Meta, and other platforms. You will work closely with our digital marketing team to drive ROI for our clients.',
      responsibilities: [
        'Create and manage PPC campaigns across multiple platforms',
        'Optimize ad spend and improve ROAS',
        'A/B test ad creatives and landing pages',
        'Analyze campaign performance and provide insights',
        'Collaborate with creative team on ad assets',
        'Manage client relationships and expectations'
      ],
      requirements: [
        '2+ years of paid advertising experience',
        'Google Ads and Meta Ads certification preferred',
        'Strong analytical and problem-solving skills',
        'Experience managing budgets of ₹5L+/month',
        'Proven track record of improving ROAS'
      ],
      skills: ['Google Ads', 'Meta Ads', 'PPC', 'Analytics', 'Campaign Management']
    },
    {
      id: '3',
      title: 'Full Stack Developer',
      department: 'IT Services',
      location: 'Surat, Gujarat',
      type: 'Full-time',
      experience: '3-6 years',
      salary: 'Competitive & Experience-Based',
      description: 'Join our development team to build cutting-edge web applications and custom solutions for clients. We work with modern technologies and follow best practices in software development.',
      responsibilities: [
        'Develop responsive web applications using React/Node.js',
        'Build custom WordPress and Shopify solutions',
        'Integrate third-party APIs and services',
        'Collaborate with designers and project managers',
        'Maintain and optimize existing applications',
        'Write clean, maintainable code with proper documentation'
      ],
      requirements: [
        '3+ years of full stack development experience',
        'Proficiency in React, Node.js, TypeScript',
        'Experience with WordPress and e-commerce platforms',
        'Strong understanding of web security and performance',
        'Portfolio of completed projects'
      ],
      skills: ['React', 'Node.js', 'TypeScript', 'WordPress', 'Shopify', 'MongoDB']
    },
    {
      id: '4',
      title: 'Social Media Strategist',
      department: 'Social Media Marketing',
      location: 'Surat, Gujarat',
      type: 'Full-time',
      experience: '2-4 years',
      salary: 'Competitive & Experience-Based',
      description: 'We are seeking a creative Social Media Strategist to develop and execute social media strategies that drive engagement and conversions for our diverse client portfolio.',
      responsibilities: [
        'Develop comprehensive social media strategies',
        'Create content calendars and manage posting schedules',
        'Analyze social media metrics and provide insights',
        'Stay updated on social media trends and best practices',
        'Manage client relationships and expectations',
        'Coordinate with content creators and designers'
      ],
      requirements: [
        '2+ years of social media marketing experience',
        'Proven track record of growing social media presence',
        'Strong understanding of major social platforms',
        'Excellent writing and communication skills',
        'Experience with social media management tools'
      ],
      skills: ['Social Media Strategy', 'Content Creation', 'Analytics', 'Community Management']
    },
    {
      id: '5',
      title: 'Content Writer',
      department: 'Content Marketing',
      location: 'Surat, Gujarat',
      type: 'Full-time',
      experience: '1-3 years',
      salary: 'Competitive & Experience-Based',
      description: 'Join our content team to create compelling content for websites, blogs, and marketing campaigns. We value creativity, research skills, and attention to detail.',
      responsibilities: [
        'Write SEO-optimized blog posts and website content',
        'Create engaging social media copy',
        'Develop email marketing campaigns',
        'Research industry trends and topics',
        'Collaborate with SEO team on content strategy',
        'Edit and proofread content for quality'
      ],
      requirements: [
        '1+ years of professional writing experience',
        'Strong portfolio of published work',
        'Understanding of SEO best practices',
        'Excellent grammar and editing skills',
        'Ability to adapt writing style for different audiences'
      ],
      skills: ['Content Writing', 'SEO Writing', 'Copywriting', 'Research', 'Editing']
    },
    {
      id: '6',
      title: 'Digital Marketing Intern',
      department: 'Multiple Departments',
      location: 'Surat, Gujarat',
      type: 'Internship',
      experience: '0-1 years',
      salary: 'Stipend Based',
      description: 'This is an excellent opportunity for students or recent graduates to learn digital marketing fundamentals while working on real client projects under expert mentorship.',
      responsibilities: [
        'Assist with SEO and content marketing tasks',
        'Support paid advertising campaigns',
        'Help manage social media accounts',
        'Conduct market research and competitor analysis',
        'Participate in client meetings and strategy sessions',
        'Learn various digital marketing tools and platforms'
      ],
      requirements: [
        'Currently pursuing or recently completed degree in Marketing/Business',
        'Basic understanding of digital marketing concepts',
        'Strong willingness to learn and take initiative',
        'Good communication and organizational skills',
        'Available for 3-6 month commitment'
      ],
      skills: ['Digital Marketing', 'Social Media', 'Content Creation', 'MS Office']
    }
  ];

  const departments = ['All Departments', 'SEO', 'Paid Marketing', 'IT Services', 'Social Media Marketing', 'Content Marketing', 'Multiple Departments'];

  const filteredJobs = selectedDepartment === 'All Departments' 
    ? jobs 
    : jobs.filter(job => job.department === selectedDepartment);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        resume: e.target.files[0]
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Application submitted:', { ...formData, job: selectedJob });
    alert('Thank you for your application! We will review it and get back to you soon.');
    setSelectedJob(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      profession: '',
      resume: null
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Build Your Digital Marketing Career at <span style={{ color: '#52658C' }}>Mirani Marketing</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-blue-100">
              At Mirani Marketing, excellence is not just a goal — it's our daily routine.
            </p>
            <p className="text-lg text-blue-100">
              We're inviting passionate, skilled, and growth-minded professionals to join our team and be part of Gujarat's most trusted digital marketing agency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Job Listings Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Search and Filter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between"
            >
              <h2 className="text-3xl font-bold text-gray-800">Jobs at Mirani Marketing</h2>
              
              <div className="relative">
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="appearance-none bg-white border-2 border-gray-300 rounded-lg px-6 py-3 pr-10 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer"
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </motion.div>

            {/* Job Cards */}
            <div className="space-y-4">
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedJob(job)}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all cursor-pointer border-l-4 border-orange-500 hover:border-l-8"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">{job.title}</h3>
                      
                      <div className="flex flex-wrap gap-3 mb-3">
                        <span className="flex items-center space-x-1 text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                          <FiBriefcase size={14} />
                          <span>{job.department}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full">
                          <FiMapPin size={14} />
                          <span>{job.location}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-sm bg-purple-50 text-purple-700 px-3 py-1 rounded-full">
                          <FiClock size={14} />
                          <span>{job.type}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-sm bg-orange-50 text-orange-700 px-3 py-1 rounded-full">
                          <FiTrendingUp size={14} />
                          <span>{job.experience}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-sm bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full">
                          <FiDollarSign size={14} />
                          <span>{job.salary}</span>
                        </span>
                      </div>

                      <p className="text-gray-600 line-clamp-2">{job.description}</p>
                    </div>

                    <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold flex items-center space-x-2 transition-all shadow-md hover:shadow-lg whitespace-nowrap">
                      <span>Apply Now</span>
                      <FiChevronRight />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-500">No jobs found in this category.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-5xl w-full my-8 shadow-2xl"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white p-8 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3">{selectedJob.title}</h2>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">{selectedJob.department}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">{selectedJob.location}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">{selectedJob.type}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">{selectedJob.experience}</span>
                  </div>
                  <p className="text-lg text-blue-100 font-semibold">
                    <FiDollarSign className="inline mr-1" />
                    {selectedJob.salary}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-white hover:bg-white/20 p-2 rounded-full transition-colors ml-4 flex-shrink-0"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-8 max-h-[70vh] overflow-y-auto">
              {/* Job Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-800">About the Role</h3>
                <p className="text-gray-700 text-lg leading-relaxed">{selectedJob.description}</p>
              </div>

              {/* Key Responsibilities */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
                  <span className="w-1 h-6 bg-orange-500 mr-3"></span>
                  Key Responsibilities
                </h4>
                <ul className="space-y-3">
                  {selectedJob.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="text-orange-500 mt-1.5 flex-shrink-0">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="text-gray-700">{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Ideal Candidate / Requirements */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
                  <span className="w-1 h-6 bg-orange-500 mr-3"></span>
                  Ideal Candidate
                </h4>
                <ul className="space-y-3">
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="text-orange-500 mt-1.5 flex-shrink-0">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Skills */}
              {selectedJob.skills && (
                <div className="mb-8">
                  <h4 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
                    <span className="w-1 h-6 bg-orange-500 mr-3"></span>
                    Required Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skills.map((skill, index) => (
                      <span key={index} className="bg-orange-50 text-orange-700 px-4 py-2 rounded-full font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Form */}
              <div className="border-t pt-8 bg-gray-50 -mx-8 px-8 pb-8 -mb-8 rounded-b-2xl">
                <h3 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                  <span className="w-1 h-8 bg-orange-500 mr-3"></span>
                  Apply Now!!
                </h3>
                <p className="text-gray-600 mb-6">Sound Like You? We'd love to hear from you.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter First Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter Last Name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Your Email *
                      </label>
                      <div className="relative">
                        <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                          placeholder="Enter Your Email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                          placeholder="+91 81234 56789"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Select Profession *
                    </label>
                    <select
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Choose</option>
                      <option value="SEO Specialist">SEO Specialist</option>
                      <option value="PPC Manager">PPC Manager</option>
                      <option value="Web Developer">Web Developer</option>
                      <option value="Social Media Manager">Social Media Manager</option>
                      <option value="Content Writer">Content Writer</option>
                      <option value="Graphic Designer">Graphic Designer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Upload File (Resume) *
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent file:mr-4 file:py-2 file:px-6 file:rounded-full file:border-0 file:bg-orange-50 file:text-orange-700 file:font-semibold hover:file:bg-orange-100 cursor-pointer transition-all"
                    />
                    <p className="text-sm text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setSelectedJob(null)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
                    >
                      Submit Now
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Don't See the Right Role?
            </h2>
            <p className="text-xl mb-8 text-gray-300 max-w-3xl mx-auto">
              We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:careers@miranimarketing.com"
                className="inline-flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
              >
                <FiMail size={20} />
                <span>careers@miranimarketing.com</span>
              </a>
              <a
                href="tel:+919313258355"
                className="inline-flex items-center space-x-2 bg-white hover:bg-gray-100 text-primary-600 px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
              >
                <FiPhone size={20} />
                <span>+91 93132 58355</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
